/* iter-shelf1 — PublishToStoreDialog.
 *
 * Lightweight dialog wired to POST /api/projects/{pid}/publish-to-stacks.
 * Genre picker + price slider + tagline + optional cover URL.
 * "Smart" flow: remembers last-used values per project in localStorage,
 * so republishing is effectively one-click.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Loader2, Rocket, X, DollarSign, ShieldAlert, ShieldCheck, BookMarked } from 'lucide-react';
import WipSamplePreviewDialog from './WipSamplePreviewDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const LS_KEY = (pid) => `bookshelf_publish_${pid}`;

const DEFAULT_PRICE_CENTS = 499;   // $4.99
const MIN_PRICE_CENTS     = 99;
const MAX_PRICE_CENTS     = 4999;

export default function PublishToStoreDialog({ card, onClose, onPublished }) {
  const { t } = useTranslation();
  const [genres, setGenres]     = useState([]);
  const [loadingGenres, setLG]  = useState(true);
  const [genreSlug, setGenreSlug] = useState('');
  const [priceCents, setPrice] = useState(DEFAULT_PRICE_CENTS);
  const [tagline, setTagline]  = useState('');
  const [coverUrl, setCoverUrl]= useState(card.cover_url || '');
  const [submitting, setSubmit] = useState(false);
  // iter-studio-premier — Voice-consistency preflight state.
  const [preflight, setPreflight] = useState(null);   // {score, drift_chapters, ...}
  const [preflightBusy, setPreflightBusy] = useState(false);
  // iter-p3-z — WIP preview dialog state.
  const [wipOpen, setWipOpen] = useState(false);

  // Fire the voice preflight in the background as soon as the dialog
  // opens. Advisory only — a low score never blocks publishing.
  useEffect(() => {
    if (!card?.project_id) return;
    let cancelled = false;
    setPreflightBusy(true);
    axios.post(`${API}/projects/${card.project_id}/publish-preflight`)
      .then((r) => { if (!cancelled) setPreflight(r.data); })
      .catch(() => { /* silent — banner just won't render */ })
      .finally(() => { if (!cancelled) setPreflightBusy(false); });
    return () => { cancelled = true; };
  }, [card?.project_id]);

  // Load last-used values for this project (smart re-publish).
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(LS_KEY(card.project_id));
      if (raw) {
        const prev = JSON.parse(raw);
        if (prev.genreSlug)   setGenreSlug(prev.genreSlug);
        if (prev.priceCents)  setPrice(prev.priceCents);
        if (prev.tagline)     setTagline(prev.tagline);
        if (prev.coverUrl)    setCoverUrl(prev.coverUrl);
      } else if (card.book) {
        // Already published once — seed from server row.
        if (card.book.genre_slug) setGenreSlug(card.book.genre_slug);
        if (card.book.price_cents) setPrice(card.book.price_cents);
        if (card.book.tagline)    setTagline(card.book.tagline);
        if (card.book.cover_url)  setCoverUrl(card.book.cover_url);
      }
    } catch { /* localStorage blocked */ }
  }, [card]);

  // Load genres.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLG(true);
      try {
        const r = await axios.get(`${API}/authors/me/bookshelf/genres`);
        if (cancelled) return;
        const rows = r.data?.genres || [];
        setGenres(rows);
        // Default genre if none picked yet: match by name to project.genre.
        setGenreSlug((cur) => {
          if (cur) return cur;
          const match = rows.find((g) => (g.name || '').toLowerCase() === String(card.genre || '').toLowerCase()
                                       || (g.slug || '').toLowerCase() === String(card.genre || '').toLowerCase());
          return match?.slug || rows[0]?.slug || '';
        });
      } catch (e) {
        toast.error('Could not load genres — try again in a moment.');
      } finally { if (!cancelled) setLG(false); }
    })();
    return () => { cancelled = true; };
  }, [card.genre]);

  const submit = async () => {
    if (!genreSlug) { toast.error(t('publish.pickShelfFirst')); return; }
    if (priceCents < MIN_PRICE_CENTS) { toast.error(t('publish.priceMin')); return; }
    setSubmit(true);
    try {
      const body = {
        genre_slug:  genreSlug,
        price_cents: priceCents,
        tagline:     (tagline || '').trim() || null,
        cover_url:   (coverUrl || '').trim() || null,
        opt_in_revenue_share: true,
      };
      await axios.post(`${API}/projects/${card.project_id}/publish-to-stacks`, body);
      // Persist for smart re-publish.
      try {
        window.localStorage.setItem(
          LS_KEY(card.project_id),
          JSON.stringify({ genreSlug, priceCents, tagline, coverUrl }),
        );
      } catch { /* ignore */ }
      toast.success(t('publish.nowLiveOnBookstore', { title: card.title }));
      onPublished?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('publish.publishFailed'));
    } finally { setSubmit(false); }
  };

  const priceUsd = (priceCents / 100).toFixed(2);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="publish-dialog"
    >
      <div className="relative w-full max-w-lg overflow-hidden rounded-lg border border-border bg-card shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground hover:bg-secondary"
          data-testid="publish-close"
          aria-label="Close publish dialog"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="border-b border-border bg-panel-header p-4">
          <div className="flex items-center gap-2">
            <Rocket className="h-4 w-4 text-primary" />
            <h2 className="text-[15px] font-semibold">{t('publish.storeTitle')}</h2>
          </div>
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            {t('publish.storeSubtitle', { title: card.title })}
          </p>
        </div>

        <div className="space-y-3 p-4">
          {/* iter-studio-premier — Voice-consistency preflight banner. */}
          {preflightBusy && !preflight && (
            <div
              className="flex items-center gap-2 rounded-sm border border-border bg-secondary/40 p-2 text-[11px] text-muted-foreground"
              data-testid="publish-preflight-loading"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('publish.voiceCheckRunning')}
            </div>
          )}
          {preflight && preflight.score >= 70 && (
            <div
              className="flex items-start gap-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 p-2 text-[11px] text-emerald-800 dark:text-emerald-200"
              data-testid="publish-preflight-ok"
              data-score={preflight.score}
            >
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <div>
                <div className="font-semibold">{t('publish.voiceCheckPassed', { score: preflight.score })}</div>
                {preflight.summary && (
                  <div className="opacity-80">{preflight.summary}</div>
                )}
              </div>
            </div>
          )}
          {preflight && preflight.score > 0 && preflight.score < 70 && (
            <div
              className="flex items-start gap-2 rounded-sm border border-amber-500/50 bg-amber-500/10 p-2 text-[11px] text-amber-900 dark:text-amber-100"
              data-testid="publish-preflight-warn"
              data-score={preflight.score}
            >
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <div className="flex-1">
                <div className="font-semibold">
                  {t('publish.voiceDriftDetected', { score: preflight.score })}
                  {preflight.drift_chapters?.length > 0
                    ? ` · ${t(preflight.drift_chapters.length === 1 ? 'publish.chapterFlagged' : 'publish.chaptersFlagged', { count: preflight.drift_chapters.length })}`
                    : ''}
                </div>
                <div className="opacity-90">
                  {preflight.summary
                    || t('publish.voiceDriftHint')}
                </div>
                {preflight.drift_chapters?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {preflight.drift_chapters.slice(0, 6).map((c) => (
                      <span key={c.number}
                            className="rounded-sm border border-amber-500/50 bg-amber-500/20 px-1 py-0.5 text-[10px]"
                            data-testid={`preflight-drift-ch-${c.number}`}>
                        Ch {c.number} · {c.score}/100
                      </span>
                    ))}
                  </div>
                )}
                {/* iter-studio-premier — One-click Voice Coach deep-link. */}
                <button
                  type="button"
                  onClick={() => {
                    try {
                      window.localStorage.setItem(
                        'pw_voice_coach_deeplink',
                        JSON.stringify({
                          project_id:  card.project_id,
                          tab:         'prose',
                          chapters:    (preflight.drift_chapters || []).map((c) => c.number),
                          at:          Date.now(),
                        }),
                      );
                    } catch { /* ignore */ }
                    window.location.assign('/app/');
                  }}
                  className="mt-1.5 inline-flex items-center gap-1 rounded-sm bg-amber-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-amber-700"
                  data-testid="publish-preflight-fix-btn"
                  title="Jump into Voice Coach with these flagged chapters preloaded"
                >
                  <ShieldAlert className="h-3 w-3" />
                  {t('publish.fixInVoiceCoach')}
                </button>
              </div>
            </div>
          )}

          {/* Genre */}
          <label className="block text-[11.5px]">
            <span className="font-semibold">{t('publish.bookstoreGenre')}</span>
            {loadingGenres ? (
              <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> {t('publish.loadingGenres')}
              </div>
            ) : (
              <select
                value={genreSlug}
                onChange={(e) => setGenreSlug(e.target.value)}
                data-testid="publish-genre-select"
                className="mt-1 w-full rounded-sm border border-border bg-card p-2 text-[12.5px]"
              >
                <option value="">{t('publish.pickShelf')}</option>
                {genres.map((g) => (
                  <option key={g.slug} value={g.slug}>{g.name}</option>
                ))}
              </select>
            )}
          </label>

          {/* Price */}
          <label className="block text-[11.5px]">
            <div className="flex items-center justify-between">
              <span className="font-semibold">{t('publish.price')}</span>
              <span className="inline-flex items-center gap-0.5 rounded-sm bg-secondary px-1.5 py-0.5 font-mono-stat text-[11px]">
                <DollarSign className="h-3 w-3" />{priceUsd}
              </span>
            </div>
            <input
              type="range"
              min={MIN_PRICE_CENTS}
              max={MAX_PRICE_CENTS}
              step={100}
              value={priceCents}
              onChange={(e) => setPrice(Number(e.target.value))}
              data-testid="publish-price-slider"
              className="mt-1 w-full accent-primary"
            />
            <div className="mt-0.5 flex justify-between text-[9.5px] text-muted-foreground">
              <span>$0.99</span><span>$24.99</span><span>$49.99</span>
            </div>
          </label>

          {/* Tagline */}
          <label className="block text-[11.5px]">
            <span className="font-semibold">{t('publish.tagline')}</span>
            <input
              type="text"
              value={tagline}
              onChange={(e) => setTagline(e.target.value.slice(0, 280))}
              placeholder={t('publish.taglinePlaceholder')}
              data-testid="publish-tagline"
              className="mt-1 w-full rounded-sm border border-border bg-card p-2 text-[12.5px] outline-none focus:border-primary"
            />
            <div className="mt-0.5 text-right text-[10px] text-muted-foreground">
              {tagline.length} / 280
            </div>
          </label>

          {/* Cover URL */}
          <label className="block text-[11.5px]">
            <span className="font-semibold">{t('publish.coverUrlOptional')}</span>
            <input
              type="url"
              value={coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              placeholder="https://…"
              data-testid="publish-cover-url"
              className="mt-1 w-full rounded-sm border border-border bg-card p-2 text-[12.5px] outline-none focus:border-primary"
            />
            {coverUrl && (
              <img
                src={coverUrl}
                alt=""
                className="mt-1 h-24 w-16 rounded-sm object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </label>

          <div className="rounded-sm border border-border bg-secondary/40 p-2 text-[10.5px] text-muted-foreground">
            {t('publish.minWordsNote')}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-panel-header/40 px-4 py-3">
          {/* iter-p3-z — WIP preview publisher. Only relevant when the
           *  card already has a live book (needs a book.id for the
           *  Companion API endpoint). */}
          {card.book?.id && (
            <button
              onClick={() => setWipOpen(true)}
              className="mr-auto inline-flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-3 py-1.5 text-[11.5px] font-semibold text-primary hover:bg-primary/20"
              data-testid="publish-wip-open"
              title="Publish or replace the free 3-chapter preview for this book. Flip 'is_wip=false' to fan out a wishlist release announcement."
            >
              <BookMarked className="h-3 w-3" />
              {t('publish.publishWipPreview')}
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-sm px-3 py-1.5 text-[11.5px] text-muted-foreground hover:text-foreground"
            data-testid="publish-cancel"
          >
            {t('notebook.cancel')}
          </button>
          <button
            onClick={submit}
            disabled={submitting || !genreSlug}
            data-testid="publish-submit"
            className="inline-flex items-center gap-1 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />}
            {card.book ? t('publish.updateOnBookstore') : t('publish.publishNow')}
          </button>
        </div>
      </div>
      {card.book?.id && (
        <WipSamplePreviewDialog
          open={wipOpen}
          onOpenChange={setWipOpen}
          book={card.book}
        />
      )}
    </div>
  );
}
